/**
 * World — the sim container. Owns every pool, the RNG bank, the spatial hash and
 * the fixed step order. Systems are pure functions over this object (§4.12 purity:
 * no wall clock, no DOM, no Math.random inside src/sim/**).
 */

import { EventBus } from '../core/events';
import { Pool } from '../core/pool';
import { SpatialHash } from '../core/spatial-hash';
import { createRngBank, type RngBank } from '../core/rng';
import { cfg } from '../core/config';
import { wrapPos } from './wrap';
import {
  createEnemy,
  createHazard,
  createPickup,
  createPlayer,
  createProjectile,
  type Enemy,
  type Hazard,
  type Pickup,
  type Player,
  type Projectile,
} from './entities';

/** §14.3 pool capacities: cap + in-flight margin. */
export const POOL_CAPACITY = {
  enemies: 512,
  projectiles: 1536,
  pickups: 544, // 512 motes + 16 health + 16 powerups
  hazards: 32,
} as const;

export interface InputState {
  /** Normalized wish direction; magnitude 0..1. */
  moveX: number;
  moveY: number;
  /** Focus aim held + world-space direction. */
  focusHeld: boolean;
  focusX: number;
  focusY: number;
  /** Edge-triggered actions consumed by the sim. */
  dashPressed: boolean;
  /** Sim tick at which each buffered action was pressed (§03.6). */
  dashBufferedTick: number;
}

export function createInputState(): InputState {
  return { moveX: 0, moveY: 0, focusHeld: false, focusX: 1, focusY: 0, dashPressed: false, dashBufferedTick: -1 };
}

export type RunOutcome = 'running' | 'victory' | 'defeat';

export interface RunCounters {
  enemies_alive: number;
  projectiles_alive: number;
  pickups_alive: number;
  hazards_alive: number;
  kills: number;
  elite_kills: number;
  boss_kills: number;
  damage_dealt: number;
  damage_taken: number;
  motes_collected: number;
  peak_surge: number;
  hash_candidate_checks: number;
  steps_dropped: number;
  /** Per-weapon damage attribution for the run summary (§13.1.10). */
  damage_by_weapon: Record<string, number>;
}

export function createCounters(): RunCounters {
  return {
    enemies_alive: 0,
    projectiles_alive: 0,
    pickups_alive: 0,
    hazards_alive: 0,
    kills: 0,
    elite_kills: 0,
    boss_kills: 0,
    damage_dealt: 0,
    damage_taken: 0,
    motes_collected: 0,
    peak_surge: 1,
    hash_candidate_checks: 0,
    steps_dropped: 0,
    damage_by_weapon: {},
  };
}

/** Hit-stop request queue — sim events dilate sim time (§03.10), determinism preserved. */
export interface HitStopState {
  frames_remaining: number;
  /** Rolling 1 s budget: ≤ 8 hit-stop frames (§03.10 cap). */
  spent_in_window: number;
  window_start_tick: number;
}

/** Screen-shake trauma lives in the sim so replays reproduce it; consumes `vfx` stream only. */
export interface FeedbackState {
  trauma: number;
  hitstop: HitStopState;
}

export class World {
  readonly bus = new EventBus();
  readonly enemies: Pool<Enemy>;
  readonly projectiles: Pool<Projectile>;
  readonly pickups: Pool<Pickup>;
  readonly hazards: Pool<Hazard>;
  readonly hash: SpatialHash;
  player: Player;
  rng: RngBank;
  input: InputState = createInputState();
  counters: RunCounters = createCounters();
  feedback: FeedbackState = { trauma: 0, hitstop: { frames_remaining: 0, spent_in_window: 0, window_start_tick: 0 } };

  tick = 0;
  runSeed = 0;
  outcome: RunOutcome = 'running';
  /** Sim is halted while a modal (level-up, pause, boss intro) is open. */
  paused = false;
  /** Ticks the sim has been frozen for boss intro framing (§13.1.7). */
  cinematicUntil = 0;
  nextEntityId = 1;
  difficultyId = 'diff.standard';
  /** AMD-51 — §8 accrual multiplier while `anomaly.swarm-surge` runs. 1 otherwise. */
  anomalyThreatScale = 1;
  /**
   * AMD-54 — the two presentation seeds, which the loadout may reroll away from `runSeed`.
   * Both default to `runSeed`, so a run started from a bare seed is bit-identical to one
   * started before this existed. Neither feeds an RNG stream: they are inputs to the pure
   * `makeCaptain` and `hullParamsForSeed` derivations, so the sim's draw order cannot move.
   */
  captainSeed = 0;
  hullSeed = 0;
  arenaHalfW: number;
  arenaHalfH: number;

  /** Scratch buffers — allocated once, reused every tick (zero-alloc rule §14.3). */
  readonly scratchA = new Int32Array(2048);
  readonly scratchB = new Int32Array(2048);
  /** Dedupe stamps for spatial-hash queries; stamp[i] === queryStamp means "seen". */
  readonly seenStamp = new Int32Array(POOL_CAPACITY.enemies);
  queryStamp = 1;

  /** Systems register their per-tick work here; order is the step order. */
  private systems: { name: string; fn: (w: World) => void }[] = [];

  constructor(seed: number) {
    this.runSeed = seed >>> 0;
    this.captainSeed = this.runSeed;
    this.hullSeed = this.runSeed;
    this.rng = createRngBank(this.runSeed);
    this.enemies = new Pool('enemies', POOL_CAPACITY.enemies, createEnemy);
    this.projectiles = new Pool('projectiles', POOL_CAPACITY.projectiles, createProjectile);
    this.pickups = new Pool('pickups', POOL_CAPACITY.pickups, createPickup);
    this.hazards = new Pool('hazards', POOL_CAPACITY.hazards, createHazard);
    this.arenaHalfW = cfg.get('arena.width') / 2;
    this.arenaHalfH = cfg.get('arena.height') / 2;
    this.hash = new SpatialHash(cfg.get('arena.width'), cfg.get('arena.height'), 4, POOL_CAPACITY.enemies + 64);
    this.player = createPlayer();
    this.player.max_hp = cfg.get('player.max_hp');
    this.player.hp = this.player.max_hp;
    this.player.hitbox_radius = cfg.get('player.hitbox_radius');
    this.player.id = this.nextEntityId++;
  }

  /**
   * Register a system. Order of registration IS the step order and is part of the
   * determinism contract — never register conditionally on non-sim state.
   */
  register(name: string, fn: (w: World) => void): void {
    this.systems.push({ name, fn });
  }

  /**
   * Pure: no wall clock, no DOM (§4.12). Called exactly once per sim tick.
   *
   * The run clock only advances on a tick that actually simulates. Assigning `tick`
   * before the pause guard would let the §13.1.4 timer and every tick-deadline in the
   * sim (cooldowns, telegraphs, i-frames) drift forward while a modal is open.
   */
  step(tick: number): void {
    if (this.paused || this.outcome !== 'running') return;
    this.tick = tick;
    this.rebuildHash();
    for (let i = 0; i < this.systems.length; i++) this.systems[i].fn(this);
    this.updateCounters();
  }

  private rebuildHash(): void {
    const h = this.hash;
    h.begin();
    const list = this.enemies.liveIndices();
    for (let i = 0; i < list.length; i++) {
      const e = this.enemies.slots[list[i]];
      h.insert(e.pool_index, e.pos.x, e.pos.y, e.hitbox_radius);
    }
    h.finish();
  }

  private updateCounters(): void {
    this.counters.enemies_alive = this.enemies.count;
    this.counters.projectiles_alive = this.projectiles.count;
    this.counters.pickups_alive = this.pickups.count;
    this.counters.hazards_alive = this.hazards.count;
    this.counters.hash_candidate_checks = this.hash.candidateChecks;
  }

  /** Next stamp for a dedupe pass over spatial-hash results. */
  nextStamp(): number {
    this.queryStamp++;
    if (this.queryStamp > 0x7ffffff0) {
      this.seenStamp.fill(0);
      this.queryStamp = 1;
    }
    return this.queryStamp;
  }

  allocId(): number {
    return this.nextEntityId++;
  }

  /**
   * Canonicalise a position into the fundamental domain `[-100, 100)` (AMD-24). The arena is a
   * flat torus, so this wraps rather than clamps: there is no wall to be pushed off.
   * `radius` is accepted for call-site symmetry and deliberately unused — a body straddling the
   * seam is not "outside" anything.
   */
  wrapToArena(pos: { x: number; y: number }, _radius: number): void {
    wrapPos(pos);
  }

  get runSeconds(): number {
    return this.tick / 60;
  }

  get runMinute(): number {
    return Math.floor(this.tick / 3600);
  }

  reset(seed: number): void {
    this.runSeed = seed >>> 0;
    // The integrator overwrites these straight after `reset` when the loadout carries its
    // own; defaulting here keeps every other caller (tests, probes) on the old behaviour.
    this.captainSeed = this.runSeed;
    this.hullSeed = this.runSeed;
    this.rng = createRngBank(this.runSeed);
    this.enemies.reset();
    this.projectiles.reset();
    this.pickups.reset();
    this.hazards.reset();
    this.bus.reset();
    this.counters = createCounters();
    this.feedback = { trauma: 0, hitstop: { frames_remaining: 0, spent_in_window: 0, window_start_tick: 0 } };
    this.tick = 0;
    this.outcome = 'running';
    this.paused = false;
    this.cinematicUntil = 0;
    this.nextEntityId = 1;
    this.queryStamp = 1;
    this.seenStamp.fill(0);
    this.player = createPlayer();
    this.player.max_hp = cfg.get('player.max_hp');
    this.player.hp = this.player.max_hp;
    this.player.hitbox_radius = cfg.get('player.hitbox_radius');
    this.player.id = this.allocId();
    this.input = createInputState();
  }
}
